import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Box,
  Chip,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
} from "@mui/material";
import { NoteAlt, Star, Delete } from "@mui/icons-material";
import { Button } from "../../design-system";
import { useUpsertMatchNote, useDeleteMatchNote } from "../../hooks/queries/useScouting";
import colors from "../../design-system/colors";

const POSICIONES = [
  "Portero", "Defensa Central", "Lateral Derecho", "Lateral Izquierdo",
  "Mediocentro Defensivo", "Mediocentro", "Mediapunta",
  "Extremo Derecho", "Extremo Izquierdo", "Delantero Centro",
];

/**
 * MatchNoteDialog — Modal to write/edit/delete notes about a player in a specific match.
 *
 * Props:
 *  - open, match, player, userId, existingNote (full note object or null), onClose
 */
const MatchNoteDialog = ({ open, match, player, userId, existingNote, onClose }) => {
  const { t, i18n } = useTranslation('scouting');
  const theme = useTheme();
  const [notes, setNotes] = useState("");
  const [titular, setTitular] = useState(null); // "titular" | "suplente" | null
  const [posicion, setPosicion] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);

  const { mutate: saveNote, isPending: saving } = useUpsertMatchNote();
  const { mutate: removeNote, isPending: deleting } = useDeleteMatchNote();

  useEffect(() => {
    if (open) {
      setNotes(existingNote?.notes || "");
      setTitular(existingNote?.titular || null);
      setPosicion(existingNote?.posicion || "");
      setConfirmDelete(false);
    }
  }, [existingNote, open]);

  const partidoId = match
    ? String(match.codPartido || match.codacta || `${match.codEquipoLocal}_${match.codEquipoVisitante}_${match.fecha}`)
    : "";

  const handleSave = () => {
    if (!userId || !player || !match) return;
    saveNote(
      {
        userId,
        jugadorId: player.jugadorId,
        partidoId,
        noteData: {
          notes,
          titular,
          posicion,
          jugadorNombre: player.jugadorNombre,
          equipoLocal: match.equipoLocal || "",
          equipoVisitante: match.equipoVisitante || "",
          fecha: match.fechaISO || match.fecha || null,
          competicion: match.competicion || "",
        },
      },
      { onSuccess: onClose }
    );
  };

  const handleDelete = () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    if (!userId || !player) return;
    removeNote(
      { userId, jugadorId: player.jugadorId, partidoId },
      { onSuccess: onClose }
    );
  };

  const hasExisting = !!existingNote?.notes;
  const fechaStr = match?.fechaISO
    ? new Date(match.fechaISO).toLocaleDateString(i18n.language, {
        day: "numeric", month: "short", year: "numeric",
      })
    : "";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth aria-labelledby="scouting-match-notes-title"
      PaperProps={{ className: "scouting-workspace sc-dialog", "data-theme": theme.palette.mode }}>
      <DialogTitle id="scouting-match-notes-title">
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <NoteAlt sx={{ color: colors.primary[600] }} />
          <Box>
            <Typography variant="h6" fontWeight={700}>
              {t('components.matchNoteDialog.title')}
            </Typography>
            {player && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                <Star sx={{ fontSize: 16, color: colors.warning?.[500] || "#f59e0b" }} />
                <Typography variant="body2" color={colors.text?.secondary || colors.neutral[600]}>
                  {player.jugadorNombre}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {match && (
          <Box className="sc-match-context" sx={{
            display: "flex", alignItems: "center", gap: 1, mb: 2,
            p: 1.5, borderRadius: '8px', backgroundColor: colors.neutral[50],
          }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" fontWeight={600}>
                {match.equipoLocal || "Local"} vs {match.equipoVisitante || "Visitante"}
              </Typography>
              <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
                {fechaStr && (
                  <Chip label={fechaStr} size="small" sx={{ height: 20, fontSize: "0.65rem" }} />
                )}
                {match.competicion && (
                  <Chip label={match.competicion} size="small" variant="outlined"
                    sx={{ height: 20, fontSize: "0.65rem" }} />
                )}
              </Box>
            </Box>
          </Box>
        )}

        {/* Titular / Suplente */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" fontWeight={600} color={colors.neutral[600]} sx={{ mb: 0.5, display: "block" }}>
            {t('components.matchNoteDialog.subtitle')}
          </Typography>
          <ToggleButtonGroup
            value={titular}
            exclusive
            onChange={(_, v) => setTitular(v)}
            size="small"
            aria-label={t("components.matchNoteDialog.subtitle")}
          >
            <ToggleButton value="titular" sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.8rem" }}>
              {t('components.matchNoteDialog.starter')}
            </ToggleButton>
            <ToggleButton value="suplente" sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.8rem" }}>
              {t('components.matchNoteDialog.substitute')}
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Posición */}
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>{t('components.matchNoteDialog.positionLabel')}</InputLabel>
          <Select
            value={posicion}
            label={t('components.matchNoteDialog.positionLabel')}
            onChange={(e) => setPosicion(e.target.value)}
          >
            <MenuItem value="">
              <em>{t('components.matchNoteDialog.positionPlaceholder')}</em>
            </MenuItem>
            {POSICIONES.map((pos) => (
              <MenuItem key={pos} value={pos}>{t(`workspace.positionLabels.${pos}`, { defaultValue: pos })}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Notes */}
        <TextField
          multiline
          rows={5}
          fullWidth
          label={t("workspace.notesLabel")}
          placeholder={t('components.matchNoteDialog.notesPlaceholder')}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          variant="outlined"
          inputProps={{ maxLength: 3000 }}
        />
        <Typography
          variant="caption"
          color={colors.neutral[400]}
          sx={{ display: "block", mt: 0.5, textAlign: "right" }}
        >
          {notes.length}/3000
        </Typography>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1, justifyContent: "space-between" }}>
        <Box>
          {hasExisting && (
            <Button
              variant="outlined"
              color="error"
              onClick={handleDelete}
              disabled={deleting || saving}
              startIcon={deleting ? <CircularProgress size={14} color="inherit" /> : <Delete />}
              sx={{ color: colors.error?.main || "#d32f2f", borderColor: colors.error?.[300] || "#ef9a9a" }}
            >
              {confirmDelete ? t('components.matchNoteDialog.confirmDelete') : t('components.matchNoteDialog.delete')}
            </Button>
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="outlined" onClick={onClose} disabled={saving || deleting}>
            {t('components.matchNoteDialog.cancel')}
          </Button>
          <Button
            variant="contained"
            className="sc-primary"
            onClick={handleSave}
            disabled={saving || deleting}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {t('components.matchNoteDialog.save')}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default MatchNoteDialog;
